<template>
  <transition name="popup-fade">
    <div v-if="isVisible && !isExpired" class="popup-overlay" @click.self="closePopup">
      <div class="popup-scroll-container">
        <div class="popup-container">
          <!-- Close button -->
          <button class="popup-close" @click="closePopup">✕</button>

          <!-- Header -->
          <div class="popup-header">
            <div class="popup-icon">🔥</div>
            <h2 class="popup-title">Специальное предложение!</h2>
            <p class="popup-subtitle">Только для новых пользователей</p>
          </div>

          <!-- Timer Section -->
          <div class="timer-section">
            <div class="timer-label">Предложение истекает через:</div>
            <div class="timer-display">
              <div class="timer-block">
                <div class="timer-value">{{ formatNumber(hours) }}</div>
                <div class="timer-unit">часов</div>
              </div>
              <div class="timer-separator">:</div>
              <div class="timer-block">
                <div class="timer-value">{{ formatNumber(minutes) }}</div>
                <div class="timer-unit">минут</div>
              </div>
            </div>
          </div>

          <!-- Discount Section -->
          <div class="discount-section">
            <div class="discount-badge">
              <div class="discount-percent">-60%</div>
              <div class="discount-text">СКИДКА</div>
            </div>
            <div class="discount-details">
              <div class="discount-title">Премиум на любой период</div>
              <div class="discount-description">
                Активируй подписку прямо сейчас с максимальной скидкой
              </div>
            </div>
          </div>

          <!-- Action Button -->
          <button class="buy-btn" @click="goToBuy">
            <span class="btn-content">Активировать со скидкой 60%</span>
            <span class="btn-shine"></span>
          </button>

          <!-- Small hint -->
          <div class="timer-hint">
            ⏰ Не упусти шанс — акция действует только 3 часа!
          </div>

        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { haptic } from '@/utils/telegram';

const router = useRouter();

const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['close']);

// Timer state
const hours = ref(0);
const minutes = ref(0);
const isExpired = ref(false);
let timerInterval = null;

// Format number to always show 2 digits
const formatNumber = (num) => {
  return num < 10 ? `0${num}` : `${num}`;
};

// Check if promo is still valid and calculate remaining time
const checkPromoValidity = () => {
  const firstLoginStr = sessionStorage.getItem("first_login");

  if (!firstLoginStr) {
    console.log('first_login не найден в sessionStorage');
    isExpired.value = true;
    return false;
  }

  const firstLoginTimestamp = parseInt(firstLoginStr);

  if (isNaN(firstLoginTimestamp)) {
    console.error('Невалидный first_login timestamp:', firstLoginStr);
    isExpired.value = true;
    return false;
  }

  // Текущее время в секундах
  const currentTimestamp = Math.floor(Date.now() / 1000);

  // Разница во времени
  const elapsedSeconds = currentTimestamp - firstLoginTimestamp;

  // 3 часа в секундах
  const threeHoursInSeconds = 3 * 60 * 60;

  console.log('First login:', new Date(firstLoginTimestamp * 1000));
  console.log('Current time:', new Date(currentTimestamp * 1000));
  console.log('Elapsed seconds:', elapsedSeconds);
  console.log('3 hours in seconds:', threeHoursInSeconds);

  // Проверяем, прошло ли меньше 3 часов
  if (elapsedSeconds < threeHoursInSeconds) {
    // Вычисляем оставшееся время
    const remainingSeconds = threeHoursInSeconds - elapsedSeconds;

    hours.value = Math.floor(remainingSeconds / 3600);
    minutes.value = Math.floor((remainingSeconds % 3600) / 60);

    console.log(`Осталось: ${hours.value}ч ${minutes.value}м`);

    isExpired.value = false;
    return true;
  } else {
    console.log('Акция истекла');
    isExpired.value = true;
    return false;
  }
};

// Initialize timer
const initTimer = () => {
  // Первоначальная проверка
  if (!checkPromoValidity()) {
    return;
  }

  // Обновляем каждую минуту
  timerInterval = setInterval(() => {
    if (!checkPromoValidity()) {
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
      // Закрываем попап если время истекло
      emit('close');
    }
  }, 60000); // Обновляем каждую минуту
};

const goToBuy = () => {
  haptic.impact();
  router.push('/buyVPN');
  emit('close');
};

const closePopup = () => {
  haptic.impact();
  emit('close');
};

// Watch for visibility changes
watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    document.body.style.overflow = 'hidden';
    checkPromoValidity();
    if (!isExpired.value) {
      initTimer();
    }
  } else {
    document.body.style.overflow = '';
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }
});

// Блокировка скролла body при открытии попапа
onMounted(() => {
  if (props.isVisible) {
    document.body.style.overflow = 'hidden';
    checkPromoValidity();
    if (!isExpired.value) {
      initTimer();
    }
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
});
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 0;
  overflow: hidden;
}

.popup-scroll-container {
  width: 100%;
  max-width: 440px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  padding-top: 80px;
  padding-bottom: 20px;
  box-sizing: border-box;
}

.popup-container {
  background: rgba(26, 31, 41, 0.95);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 28px 24px 24px 24px;
  width: 100%;
  max-width: 90%;
  position: relative;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 0 0.5px rgba(255, 255, 255, 0.1) inset;
  margin: 0 auto 140px auto;
  min-height: min-content;
  flex-shrink: 0;
}

@supports (padding: max(0px)) {
  .popup-scroll-container {
    padding-top: max(80px, calc(env(safe-area-inset-top) + 60px));
    padding-left: max(20px, env(safe-area-inset-left));
    padding-right: max(20px, env(safe-area-inset-right));
    padding-bottom: max(20px, env(safe-area-inset-bottom));
  }
}

@media (max-width: 768px) {
  .popup-scroll-container {
    padding-top: max(80px, calc(env(safe-area-inset-top, 0px) + 60px));
  }

  .popup-container {
    margin-bottom: 160px;
  }
}

@media (max-height: 700px) {
  .popup-scroll-container {
    padding-top: 70px;
  }

  .popup-container {
    margin-bottom: 140px;
    padding: 24px 20px 20px 20px;
  }

  .popup-icon {
    font-size: 52px !important;
    margin-bottom: 8px !important;
  }

  .popup-title {
    font-size: 22px !important;
  }
}

@media (max-height: 600px) {
  .popup-scroll-container {
    padding-top: 60px;
  }

  .popup-container {
    padding: 20px 18px 18px 18px;
  }

  .popup-header {
    margin-bottom: 16px !important;
  }

  .timer-section,
  .discount-section {
    padding: 16px !important;
    margin-bottom: 16px !important;
  }
}

@media (min-width: 769px) {
  .popup-scroll-container {
    align-items: center;
    padding-top: 20px;
  }

  .popup-container {
    margin-bottom: 20px;
  }
}

@media (display-mode: fullscreen) {
  .popup-scroll-container {
    padding-top: 90px;
  }
}

@media (max-width: 430px) and (min-height: 800px) {
  .popup-scroll-container {
    padding-top: max(90px, calc(env(safe-area-inset-top) + 70px));
  }
}

.popup-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.popup-close:active {
  transform: scale(0.9);
  background: rgba(255, 255, 255, 0.2);
}

.popup-header {
  text-align: center;
  margin-bottom: 24px;
}

.popup-icon {
  font-size: 64px;
  margin-bottom: 12px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.popup-title {
  font-size: 26px;
  font-weight: 800;
  margin-bottom: 8px;
  margin-top: 0px;
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.popup-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin: 0;
}

/* Timer Section */
.timer-section {
  background: rgba(255, 107, 53, 0.15);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 107, 53, 0.3);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(255, 107, 53, 0.2);
}

.timer-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-bottom: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timer-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.timer-block {
  background: rgba(26, 31, 41, 0.8);
  border: 1px solid rgba(255, 107, 53, 0.4);
  border-radius: 12px;
  padding: 16px 20px;
  min-width: 90px;
  text-align: center;
}

.timer-value {
  font-size: 36px;
  font-weight: 800;
  color: #FF8555;
  line-height: 1;
  font-family: 'Courier New', monospace;
}

.timer-unit {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timer-separator {
  font-size: 32px;
  font-weight: 700;
  color: #FF8555;
  line-height: 1;
}

/* Discount Section */
.discount-section {
  background: rgba(117, 216, 185, 0.15);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(117, 216, 185, 0.3);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(117, 216, 185, 0.2);
  display: flex;
  align-items: center;
  gap: 16px;
}

.discount-badge {
  background: linear-gradient(135deg, #75d8b9, #5bc9a8);
  border-radius: 12px;
  padding: 12px 16px;
  text-align: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(117, 216, 185, 0.3);
}

.discount-percent {
  font-size: 28px;
  font-weight: 900;
  color: white;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.discount-text {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 4px;
  font-weight: 700;
  letter-spacing: 1px;
}

.discount-details {
  flex: 1;
}

.discount-title {
  font-size: 17px;
  font-weight: 700;
  color: #75d8b9;
  margin-bottom: 4px;
  line-height: 1.2;
}

.discount-description {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.4;
}

/* Benefits Section */
.benefits-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 20px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  line-height: 1.4;
}

.benefit-item:last-child {
  margin-bottom: 0;
}

.benefit-icon {
  font-size: 18px;
  flex-shrink: 0;
}

/* Buy Button */
.buy-btn {
  position: relative;
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  color: white;
  padding: 18px;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 32px rgba(255, 107, 53, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.3) inset;
  overflow: hidden;
  margin-bottom: 12px;
}

.btn-content {
  position: relative;
  z-index: 2;
  display: block;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.btn-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    135deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 45%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0.2) 55%,
    transparent 100%
  );
  transform: translateX(-100%) translateY(-100%) rotate(45deg);
  animation: shine 3s infinite;
  pointer-events: none;
}

@keyframes shine {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  50% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
  100% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
}

.buy-btn:active {
  transform: scale(0.98);
  box-shadow:
    0 4px 16px rgba(255, 107, 53, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
}

.timer-hint {
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}

/* Transitions */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}

.popup-fade-enter-active .popup-scroll-container,
.popup-fade-leave-active .popup-scroll-container {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

.popup-fade-enter-from .popup-scroll-container {
  transform: scale(0.9) translateY(20px);
}

.popup-fade-leave-to .popup-scroll-container {
  transform: scale(0.9) translateY(20px);
}

.popup-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.popup-scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.popup-scroll-container::-webkit-scrollbar-thumb {
  background: rgba(255, 107, 53, 0.4);
  border-radius: 3px;
}

.popup-scroll-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 107, 53, 0.6);
}
</style>